import { Head, router, useForm } from "@inertiajs/react"

import AgentArchetypeForm from "@/components/agent-archetypes/agent-archetype-form"
import RecordForm from "@/components/record-form"
import RecordPageHeader from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import { adminAgentArchetypePath, adminAgentArchetypesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface AgentArchetype {
  uid: string
  name: string
  description: string | null
  long_description: string | null
  benefit: string | null
  duration: string | null
  wholesale_price: number
  illustration_svg: string | null
  icon: string | null
  outcome: string | null
  activation_graphic_url: string | null
}

export default function EditAgentArchetype({
  agent_archetype,
  outcomes,
}: {
  agent_archetype: AgentArchetype
  outcomes: string[]
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Archetypes", href: adminAgentArchetypesPath() },
    {
      title: agent_archetype.name,
      href: adminAgentArchetypePath({ id: agent_archetype.uid }),
    },
    { title: "Edit", href: "#" },
  ]

  const { data, setData, patch, processing, errors } = useForm({
    agent_archetype: {
      name: agent_archetype.name,
      description: agent_archetype.description ?? "",
      long_description: agent_archetype.long_description ?? "",
      benefit: agent_archetype.benefit ?? "",
      duration: agent_archetype.duration ?? "",
      wholesale_price: agent_archetype.wholesale_price,
      illustration_svg: agent_archetype.illustration_svg ?? "",
      icon: agent_archetype.icon ?? "",
      outcome: agent_archetype.outcome ?? "",
      activation_graphic: null as File | null,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // forceFormData so the uploaded graphic is sent as multipart (IMP-244).
    patch(adminAgentArchetypePath({ id: agent_archetype.uid }), {
      forceFormData: true,
    })
  }

  function handleCancel() {
    router.visit(adminAgentArchetypePath({ id: agent_archetype.uid }))
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${agent_archetype.name}`} />
      <div className="flex h-full w-full flex-col gap-6 p-4">
        <div className="mx-auto w-full max-w-2xl">
          <RecordPageHeader title={agent_archetype.name} />
        </div>
        <RecordForm
          submitLabel="Save"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <AgentArchetypeForm
            data={data}
            setData={setData}
            errors={errors}
            outcomes={outcomes}
            activationGraphicUrl={agent_archetype.activation_graphic_url}
          />
        </RecordForm>
      </div>
    </AdminLayout>
  )
}
